import { useEffect, useState } from "react";
import axios from "axios";
import { Lock, User, Loader2 } from "lucide-react";
import { API, CODE_KEY, AGENT_KEY, ROLE_KEY, FOCUS, ecrireStockage, prendreMotifDeconnexion } from "./outils";

// Connexion par code partagé désactivée (décision du 22/10/2026) : chaque agent se connecte
// avec son identifiant. Réactivable à la construction avec REACT_APP_CONNEXION_CODE=1.
const CONNEXION_PAR_CODE = process.env.REACT_APP_CONNEXION_CODE === "1";

export default function EcranLogin({ onOk }) {
  const [mode, setMode] = useState("agent"); // agent | code
  const [identifiant, setIdentifiant] = useState("");
  const [mdp, setMdp] = useState("");
  const [code, setCode] = useState("");
  const [erreur, setErreur] = useState(prendreMotifDeconnexion);
  const [chargement, setChargement] = useState(false);

  // 2.3.1 : ouverture depuis le bouton « Application web » du logiciel du bureau (code à usage unique, 60 s)
  useEffect(() => {
    const entree = new URLSearchParams(window.location.search).get("entree");
    if (!entree) return;
    window.history.replaceState(null, "", window.location.pathname);   // le code ne reste pas dans l'adresse
    setChargement(true);
    axios
      .post(`${API}/mobile/entree-windows`, { code: entree })
      .then(({ data }) => {
        ecrireStockage(CODE_KEY, data.token);
        ecrireStockage(AGENT_KEY, data.identifiant);
        ecrireStockage(ROLE_KEY, data.role || "");
        onOk(data.token, data.identifiant, data.role || "");
      })
      .catch((err) => {
        const d = err?.response?.data?.detail;
        setErreur(typeof d === "string" && d ? d : "Ouverture impossible : cliquez de nouveau sur « Application web » dans le logiciel.");
      })
      .finally(() => setChargement(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const valider = async (e) => {
    e.preventDefault();
    setErreur("");
    setChargement(true);
    try {
      if (mode === "agent") {
        const { data } = await axios.post(`${API}/mobile/login-agent`, {
          identifiant,
          mot_de_passe: mdp,
        });
        ecrireStockage(CODE_KEY, data.token);
        ecrireStockage(AGENT_KEY, data.identifiant);
        ecrireStockage(ROLE_KEY, data.role || "");
        onOk(data.token, data.identifiant, data.role || "");
      } else {
        await axios.post(`${API}/mobile/login`, { code });
        ecrireStockage(CODE_KEY, code);
        ecrireStockage(AGENT_KEY, "");
        ecrireStockage(ROLE_KEY, "");
        onOk(code, "", "");
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      setErreur(
        typeof d === "string" && d
          ? d
          : !err?.response
            ? "Erreur de connexion. Réessayez."
            : mode === "agent"
              ? "Identifiant ou mot de passe incorrect."
              : "Code incorrect. Réessayez."
      );
    } finally {
      setChargement(false);
    }
  };

  const champ = `w-full h-12 bg-[#1E293B] border border-[#334155] text-white rounded-xl px-4 text-[15px] outline-none focus:border-[#2DD4BF] focus:ring-2 focus:ring-[#2DD4BF]/40 placeholder:text-[#94A3B8]`;

  return (
    <div className="ecran overflow-y-auto flex flex-col items-center px-6 py-10 bg-[#0F172A]">
      <div className="w-full max-w-sm text-center my-auto">
        <div className="mx-auto w-24 h-24 rounded-2xl overflow-hidden mb-5 shadow-lg ring-1 ring-white/10">
          <img
            src={`${process.env.PUBLIC_URL || ""}/logo.png`}
            alt="Khaldoun Office"
            className="w-full h-full object-cover"
            data-testid="logo-login"
          />
        </div>
        <h1 className="text-2xl font-bold text-white">Khaldoun Office</h1>
        <p className="text-[#94A3B8] text-sm mt-1 mb-8">Suivi des demandes de visa</p>
        <form onSubmit={valider} className="space-y-3 text-left" noValidate>
          {mode === "agent" ? (
            <>
              <label htmlFor="login-identifiant" className="sr-only">
                Identifiant
              </label>
              <div className="relative">
                <User aria-hidden="true" className="w-5 h-5 text-[#94A3B8] absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  id="login-identifiant"
                  data-testid="input-identifiant"
                  autoFocus
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="username"
                  value={identifiant}
                  onChange={(e) => setIdentifiant(e.target.value)}
                  placeholder="Identifiant"
                  className={`${champ} pl-12`}
                />
              </div>
              <label htmlFor="login-mdp" className="sr-only">
                Mot de passe
              </label>
              <div className="relative">
                <Lock aria-hidden="true" className="w-5 h-5 text-[#94A3B8] absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  id="login-mdp"
                  data-testid="input-mdp"
                  type="password"
                  autoComplete="current-password"
                  value={mdp}
                  onChange={(e) => setMdp(e.target.value)}
                  placeholder="Mot de passe"
                  className={`${champ} pl-12`}
                />
              </div>
            </>
          ) : (
            <>
              <label htmlFor="login-code" className="sr-only">
                Code d'accès
              </label>
              <div className="relative">
                <Lock aria-hidden="true" className="w-5 h-5 text-[#94A3B8] absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  id="login-code"
                  data-testid="input-code"
                  type="password"
                  autoCapitalize="off"
                  autoComplete="off"
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Code d'accès"
                  className={`${champ} text-center text-lg tracking-widest pl-12`}
                />
              </div>
            </>
          )}
          {erreur ? (
            <p role="alert" className="text-[#FCA5A5] text-sm text-center" data-testid="login-erreur">
              {erreur}
            </p>
          ) : null}
          <button
            type="submit"
            disabled={chargement || (mode === "agent" ? !identifiant || !mdp : !code)}
            data-testid="btn-login"
            className={`w-full h-[52px] flex items-center justify-center gap-2 bg-[#0F766E] hover:bg-[#115E59] disabled:opacity-50 text-white text-base font-bold rounded-xl transition-colors ${FOCUS}`}
          >
            {chargement ? (
              <>
                <Loader2 aria-hidden="true" className="w-5 h-5 animate-spin" /> Vérification…
              </>
            ) : (
              "Se connecter"
            )}
          </button>
        </form>
        {CONNEXION_PAR_CODE && (
        <button
          type="button"
          onClick={() => {
            setMode((m) => (m === "agent" ? "code" : "agent"));
            setErreur("");
          }}
          data-testid="btn-basculer-mode"
          className={`mt-4 min-h-[44px] px-3 text-sm text-[#94A3B8] hover:text-[#5EEAD4] underline rounded-lg ${FOCUS}`}
        >
          {mode === "agent" ? "Utiliser un code d'accès" : "Connexion par identifiant et mot de passe"}
        </button>
        )}
      </div>
    </div>
  );
}
